import React from "react";
import { CheckCheck } from "lucide-react";

const initials = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("");

// A genuine WhatsApp-style chat note (dark theme). The client's message sits on
// the left and DotSlashLab's reply on the right, so the full width is used.
// Colors are inline so they stay exact regardless of the surrounding theme.
export function WhatsAppNote({
  name,
  message,
  time = "",
  reply,
  replyTime = "",
}: {
  name: string;
  message: string;
  time?: string;
  reply?: string;
  replyTime?: string;
}) {
  return (
    <div
      className="w-full rounded-2xl overflow-hidden border border-white/10 shadow-2xl"
      style={{ background: "#0b141a" }}
    >
      {/* Chat header */}
      <div className="flex items-center gap-3 px-4 py-3" style={{ background: "#202c33" }}>
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold shrink-0"
          style={{ background: "#2a3942", color: "#e9edef" }}
        >
          {initials(name)}
        </div>
        <div className="leading-tight min-w-0">
          <p className="text-[13px] font-medium truncate" style={{ color: "#e9edef" }}>{name}</p>
          <p className="text-[11px]" style={{ color: "#8696a0" }}>online</p>
        </div>
      </div>

      {/* Chat body */}
      <div
        className="px-4 py-6"
        style={{
          backgroundColor: "#0b141a",
          backgroundImage: "radial-gradient(rgba(255,255,255,0.035) 1px, transparent 1px)",
          backgroundSize: "18px 18px",
        }}
      >
        <div className="grid gap-4 md:grid-cols-2 md:gap-10 items-start">
          {/* Incoming: the client's message (left) */}
          <div className="flex">
            <div
              className="relative w-full max-w-md rounded-lg rounded-tl-sm px-3 py-2"
              style={{ background: "#202c33" }}
            >
              <p className="text-[11px] font-semibold mb-0.5" style={{ color: "#53bdeb" }}>
                {name.split(/\s+/)[0]}
              </p>
              <p className="text-[13.5px] leading-relaxed" style={{ color: "#e9edef" }}>
                {message}
              </p>
              <div className="flex items-center justify-end gap-1 mt-1 -mb-0.5">
                <span className="text-[10px]" style={{ color: "#8696a0" }}>{time}</span>
                <CheckCheck className="w-4 h-4" style={{ color: "#53bdeb" }} />
              </div>
            </div>
          </div>

          {/* Outgoing: DotSlashLab's reply (right) */}
          {reply && (
            <div className="flex md:justify-end">
              <div
                className="relative w-full max-w-md rounded-lg rounded-tr-sm px-3 py-2"
                style={{ background: "#005c4b" }}
              >
                <p className="text-[11px] font-semibold mb-0.5" style={{ color: "#9ad6c4" }}>
                  DotSlashLab
                </p>
                <p className="text-[13.5px] leading-relaxed" style={{ color: "#e9edef" }}>
                  {reply}
                </p>
                <div className="flex items-center justify-end gap-1 mt-1 -mb-0.5">
                  <span className="text-[10px]" style={{ color: "rgba(233,237,239,0.55)" }}>{replyTime}</span>
                  <CheckCheck className="w-4 h-4" style={{ color: "#53bdeb" }} />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
